import React, { useCallback, useEffect, useState } from "react";
import Text from "@src/lib/ui/Text";
import Link from "next/link";

import ChatBubbleOutlineIcon from "@src/lib/icons/18px/chat-bubble-outline";
import HomeFillIcon from "@src/lib/icons/18px/home-fill";
import HomeOutlineIcon from "@src/lib/icons/18px/home-outline";
import WindowChartLineFillIcon from "@src/lib/icons/18px/window-chart-line-fill";
import WindowChartLineOutlineIcon from "@src/lib/icons/18px/window-chart-line-outline";
import SearchIcon from "@src/lib/icons/18px/search";
import ShopOutlineIcon from "@src/lib/icons/18px/shop-outline";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";

import { useRouter } from "next/router";

const AdminLeftNavMainMenu: React.FC = () => {
  const { pathname } = useRouter();
  const { userActionPermissions } = useActionPermissions();
  const [isMac, setIsMac] = useState(true);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setIsMac(
      /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform || navigator.userAgent),
    );
  }, []);

  const openSearch = useCallback(() => {
    document.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "k",
        metaKey: isMac,
        ctrlKey: !isMac,
        bubbles: true,
      }),
    );
  }, [isMac]);

  const shortcutLabel = isMac ? "⌘K" : "Ctrl K";
  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem className="mt-0.5 mb-1">
            <SidebarMenuButton
              tooltip="Search"
              onClick={openSearch}
              className="rounded-full border border-gray-3 hover:border-gray-4"
            >
              <SearchIcon />
              Search
              <Text
                variant="micro-title"
                className="ml-auto rounded-md bg-gray-2 px-1.5 py-0.5 leading-none text-gray-8 group-data-[collapsible=icon]:hidden"
              >
                {shortcutLabel}
              </Text>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Home"
              isActive={pathname === "/"}
            >
              <Link href="/">
                {pathname === "/" ? <HomeFillIcon /> : <HomeOutlineIcon />}
                Home
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {userActionPermissions?.viewReporting && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Reporting"
                isActive={pathname.startsWith("/reporting")}
              >
                <Link href="/reporting">
                  {pathname.startsWith("/reporting") ? (
                    <WindowChartLineFillIcon />
                  ) : (
                    <WindowChartLineOutlineIcon />
                  )}
                  Reporting
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Messages"
              isActive={pathname === "/messages"}
            >
              <Link href="/messages">
                <ChatBubbleOutlineIcon />
                Messages
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Marketplace"
              dataTestId="marketplace-button"
              isActive={pathname === "/marketplace"}
            >
              <Link href="/marketplace">
                <ShopOutlineIcon />
                Marketplace
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export default AdminLeftNavMainMenu;
